import { announceAuthSessionChange } from '../authSessionSync';
import { hideLoginState } from '../authGateController';
import type { PrototypeState } from '../controllerState';
import { byId, inputValue } from '../domUtils';
import { closeModal, openModal } from '../navigationController';
import { rehydrateCurrentView } from '../navigationController';
import type { Root } from '../prototypeTypes';
import { resetOAuthLinkTurnstileAfterError } from '../turnstileWidgetController';
import { setAuthButtonLoading } from './authButtonLock';
import { describeOAuthLinkFailure } from './oauthLinkErrorPolicy';
import { clearAuthModalFields } from '../formProfileController';
import { parseClientPlanContext } from '../clientPlanContext';
import { validateCurrentPassword } from '../../../domain/passwordPolicy';
import { parseCanonicalAuthenticatedSession } from '../authenticatedSessionContract';

export async function handleOAuthLinkComplete(root: Root, state: PrototypeState): Promise<void> {
	const password = inputValue(root, 'oauthLinkPassword');
	const passwordError = validateCurrentPassword(password);
	if (passwordError) { paintError(root, passwordError); return; }
	if (!state.runtime.interactiveTurnstile?.hasToken()) { paintError(root, 'Please complete the security verification first.'); return; }
	const button = byId(root, 'oauthLinkSubmitBtn') as HTMLButtonElement | null;
	const isAttemptCurrent = beginOAuthLinkAttempt(root, button);
	setAuthButtonLoading(button, true, 'Link Google and sign in', 'Linking…');
	paintError(root, null);
	try {
		const turnstileToken = await state.runtime.interactiveTurnstile.getToken('auth-google-link');
		if (!isAttemptCurrent()) return;
		await state.runtime.api.completeOAuthLink({ password, turnstileToken });
		if (!isAttemptCurrent()) return;
		const session = parseCanonicalAuthenticatedSession(await state.runtime.api.getSession());
		if (!isAttemptCurrent()) return;
		if (!session) throw new Error('oauth_link_session_missing');
		state.isLoggedIn = true;
		state.runtime.api.setAuthenticated(true);
		const planContext = parseClientPlanContext(session.viewer.planContext);
		state.planContext = planContext;
		state.currentPlan = planContext?.billingPlan ?? 'free';
		state.effectiveAccessPlan = planContext?.effectiveAccessPlan ?? 'free';
		state.runtime.currentPlan = state.effectiveAccessPlan;
		state.runtime.userId = session.viewer.userId;
		state.runtime.sessionHash = session.viewer.sessionHash;
		state.runtime.userEmail = session.viewer.email ?? '';
		state.runtime.userDisplayName = session.viewer.displayName ?? '';
		hideLoginState(root, state);
		closeModal(root, 'oauthLinkModal');
		clearAuthModalFields(root, 'oauthLink');
		announceAuthSessionChange();
		rehydrateCurrentView(root);
	} catch (error) {
		if (!isAttemptCurrent()) return;
		const failure = describeOAuthLinkFailure(error);
		paintError(root, failure.message);
		if (failure.refreshTurnstile) resetOAuthLinkTurnstileAfterError(root, state.runtime);
	} finally {
		if (isAttemptCurrent()) setAuthButtonLoading(button, false, 'Link Google and sign in');
	}
}

function beginOAuthLinkAttempt(root: Root, button: HTMLButtonElement | null): () => boolean {
	if (!button) return () => false;
	const token = crypto.randomUUID();
	button.dataset.uiAttempt = token;
	return () => button.dataset.uiAttempt === token && root.querySelector('#oauthLinkModal')?.classList.contains('active') === true;
}

export async function handleOAuthLinkCancel(root: Root, state: PrototypeState): Promise<void> {
	await state.runtime.api.cancelOAuthLink().catch(() => undefined);
	closeModal(root, 'oauthLinkModal');
	clearAuthModalFields(root, 'oauthLink');
}

export async function handleOAuthLinkForgotPassword(root: Root, state: PrototypeState): Promise<void> {
	await handleOAuthLinkCancel(root, state);
	openModal(root, 'forgotModal');
}

function paintError(root: Root, message: string | null): void {
	const node = byId(root, 'oauthLinkFormAlert');
	if (!node) return;
	node.textContent = message ?? '';
	node.hidden = !message;
}
